import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { LoadingSpinner, Button, StatusBadge } from '../components/Common';
import { useAuthContext as useAuth } from '../contexts/AuthContext';
import { canView, getAccessLevel, permissionChecks } from '../lib/auth/helpers';
import { useApi } from '../hooks/useApi';
import { getCurrentConfig } from '../config';
import toast from 'react-hot-toast';
import { 
  ArrowLeftIcon,
  ArrowPathIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  CubeIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface BeltAsset {
  unit_name: string;
  asset_id: number;
}

interface Belt {
  type: string;
  id: number;
  name: string;
  checkpoint_name: string;
  assets: BeltAsset[];
}

interface AssetStatus {
  asset_id: number;
  isOpen: boolean;
}

interface GroupedBelts {
  [checkpointName: string]: Belt[];
}

export const BeltsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth(); // Use AuthContext instead of AuthStore
  const api = useApi(); // Use authenticated API service
  const [belts, setBelts] = useState<Belt[]>([]);
  const [assetStatuses, setAssetStatuses] = useState<Record<number, boolean>>({});
  const [toggling, setToggling] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(true);

  // Load real asset statuses from API
  const loadAssetStatuses = async () => {
    try {
      const data = await api.getAssets();
      
      // Handle the correct API response structure
      let assetsArray = [];
      if (data.data && data.data.assets) {
        assetsArray = data.data.assets; // Correct path: data.data.assets
      } else if (data.data) {
        assetsArray = data.data;
      } else {
        assetsArray = data;
      }
      
      // Create lookup map: asset_id -> isOpen status
      const realStatuses: Record<number, boolean> = {};
      
      if (Array.isArray(assetsArray)) {
        assetsArray.forEach((asset: any) => {
          // Use the correct field names from API response
          const assetId = asset.asset_id;
          const isOpen = asset.isOpen;
          
          if (assetId !== undefined) {
            realStatuses[assetId] = isOpen;
          }
        });
      }
      
      setAssetStatuses(realStatuses);
    } catch (error) {
      toast.error('Failed to load real asset statuses');
    }
  };

  // Load belts data from JSON file
  useEffect(() => {
    const loadBelts = async () => {
      try {
        // Load belt structure
        const response = await fetch(`/belts.json?v=${Date.now()}`);
        const beltsData = await response.json();
        setBelts(beltsData);
        
        // Load real asset statuses from API
        await loadAssetStatuses();
        
        setLoading(false);
      } catch (error) {
        toast.error('Failed to load belts data');
        setLoading(false);
      }
    };

    loadBelts();
  }, []);

  // Group belts by checkpoint_name and sort them
  const getGroupedAndSortedBelts = (): GroupedBelts => {
    const grouped: GroupedBelts = {};
    
    // Group belts by checkpoint_name
    belts.forEach(belt => {
      const checkpointName = belt.checkpoint_name || 'UNKNOWN CHECKPOINT';
      if (!grouped[checkpointName]) {
        grouped[checkpointName] = [];
      }
      grouped[checkpointName].push(belt);
    });
    
    // Sort lanes within each checkpoint group by lane name
    Object.keys(grouped).forEach(checkpointName => {
      grouped[checkpointName].sort((a, b) => a.name.localeCompare(b.name));
    });
    
    return grouped;
  };

  // Render belts with separators between checkpoint groups
  const renderBeltsWithSeparators = () => {
    const groupedBelts = getGroupedAndSortedBelts();
    const sortedCheckpointNames = Object.keys(groupedBelts).sort();
    
    const elements: JSX.Element[] = [];
    
    sortedCheckpointNames.forEach((checkpointName, checkpointIndex) => {
      const beltsInCheckpoint = groupedBelts[checkpointName];
      
      // Add checkpoint header
      elements.push(
        <div key={`header-${checkpointName}`} className="col-span-full mb-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white">
              {checkpointName}
            </h2>
            <span className="text-sm text-gray-400">
              {beltsInCheckpoint.length} lane{beltsInCheckpoint.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      );
      
      // Add belts for this checkpoint
      beltsInCheckpoint.forEach((belt, beltIndex) => {
        const beltStatus = getBeltStatus(belt);
        const isToggling = isBeltToggling(belt);
        
        elements.push(
          <div
            key={`${checkpointName}-${belt.id}`}
            className="bg-gray-800 rounded-lg p-6 hover:bg-gray-750 transition-colors"
          >
            {/* Belt Header with Title and Checkpoint Badge */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white mb-2">
                  {belt.name}
                </h3>
                <div className="flex items-center space-x-2 mb-3">
                  <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full">
                    {checkpointName}
                  </span>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                beltStatus === 'open' 
                  ? 'bg-green-800 text-green-100'
                  : beltStatus === 'closed'
                  ? 'bg-red-800 text-red-100' 
                  : 'bg-yellow-800 text-yellow-100'
              }`}>
                {beltStatus === 'partial' ? 'Partial' : beltStatus}
              </div>
            </div>

            {/* Status Toggle - Only show if user has lane_status_update permission */}
            {canUpdateLaneStatus ? (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Status</span>
                <button
                  onClick={() => handleToggleBelt(belt, beltIndex)}
                  disabled={isToggling}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 ${
                    isToggling
                      ? 'bg-gray-500 cursor-not-allowed'
                      : beltStatus === 'open' || beltStatus === 'partial'
                      ? 'bg-green-600 focus:ring-green-500' 
                      : 'bg-gray-600 focus:ring-gray-500'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      (beltStatus === 'open' || beltStatus === 'partial') ? 'translate-x-6' : 'translate-x-1'
                    } ${isToggling ? 'animate-pulse' : ''}`}
                  />
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Status</span>
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-2 ${
                    beltStatus === 'open' || beltStatus === 'partial' ? 'bg-green-500' : 'bg-red-500'
                  }`} />
                  <span className="text-sm text-gray-400">
                    {beltStatus === 'open' ? 'Open' : beltStatus === 'partial' ? 'Partial' : 'Closed'}
                  </span>
                </div>
              </div>
            )}
          </div>
        );
      });
      
      // Add separator between checkpoint groups (except after the last one)
      if (checkpointIndex < sortedCheckpointNames.length - 1) {
        elements.push(
          <div key={`separator-${checkpointName}`} className="col-span-full border-t border-gray-600 my-6">
          </div>
        );
      }
    });
    
    return elements;
  };

  const handleToggleBelt = async (belt: Belt, beltIndex: number) => {
    // Check if user has permission to update lane status
    if (!canUpdateLaneStatus) {
      toast.error('You do not have permission to toggle lane status');
      return;
    }

    const assetIds = belt.assets.map(asset => asset.asset_id);
    const beltStatus = getBeltStatus(belt);
    
    // Check if any asset is currently being toggled
    const isAnyToggling = assetIds.some(id => toggling[id]);
    if (isAnyToggling) return;

    // Set all assets in this belt as toggling
    const newToggling = { ...toggling };
    assetIds.forEach(id => {
      newToggling[id] = true;
    });
    setToggling(newToggling);

    try {
      const baseUrl = getCurrentConfig().apiBaseUrl;
      
      // Smart toggle logic based on current belt status
      let assetsToToggle: BeltAsset[] = [];
      let targetAction = '';
      
      if (beltStatus === 'partial') {
        // PARTIAL: Close all open assets, skip closed ones
        assetsToToggle = belt.assets.filter(asset => 
          assetStatuses[asset.asset_id] === true
        );
        targetAction = 'closed';
      } else if (beltStatus === 'open') {
        // ALL OPEN: Close all
        assetsToToggle = belt.assets;
        targetAction = 'closed';
      } else {
        // ALL CLOSED: Open all
        assetsToToggle = belt.assets;
        targetAction = 'opened';
      }
      
      
      // Toggle only the assets that need to change
      for (const asset of assetsToToggle) {
        try {
          await api.toggleAsset(
            asset.asset_id,
            true, // cascade=true
            user,
            `Belt toggle: ${belt.name} ${targetAction}`
          );
          
        } catch (assetError) {
          throw new Error(`Failed to toggle asset ${asset.asset_id}`);
        }

        // Update the status for this asset
        setAssetStatuses(prev => ({
          ...prev,
          [asset.asset_id]: !prev[asset.asset_id]
        }));
      }

      toast.success(`${belt.name} ${targetAction} successfully`);
      
    } catch (error) {
      toast.error('Failed to toggle belt status');
      // Reload real statuses on error
      await loadAssetStatuses();
    } finally {
      // Clear toggling state for all assets
      const clearedToggling = { ...toggling };
      assetIds.forEach(id => {
        delete clearedToggling[id];
      });
      setToggling(clearedToggling);
    }
  };

  const getBeltStatus = (belt: Belt): 'open' | 'closed' | 'partial' => {
    const statuses = belt.assets.map(asset => assetStatuses[asset.asset_id]);
    const allOpen = statuses.every(status => status === true);
    const allClosed = statuses.every(status => status === false);
    
    if (allOpen) return 'open';
    if (allClosed) return 'closed';
    return 'partial';
  };

  const isBeltToggling = (belt: Belt): boolean => {
    return belt.assets.some(asset => toggling[asset.asset_id]);
  };

  // Check new BeltPage permissions
  const canViewBeltPage = permissionChecks.canViewBeltPage();
  const canUpdateLaneStatus = permissionChecks.canUpdateLaneStatus();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Header />
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  // If no access to lanes, show access restricted message
  if (!canViewBeltPage) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Access Restricted</h1>
            <p className="text-gray-400">You don't have permission to view lane information.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Back to Dashboard
            </button>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">Lane Management</h1>
              <p className="mt-2 text-gray-400">
                Monitor and control conveyor lane status
              </p>
            </div>
          </div>
        </div>

        {/* Belts Grid with Checkpoint Separators */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {renderBeltsWithSeparators()}
        </div>
      </main>
    </div>
  );
};